import React from "react";

import CheckIcon from "@mui/icons-material/Check";
import { Stack } from "@mui/material";

import HalfFilledCircleIcon from "@src/assets/svg/halfFilledCircle";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Text from "@src/sharedComponents/Typography/Text";

import {
  getStepStatus,
  type ProgressStep,
  type StepStatus,
  TransitionProgressIndicatorProps,
} from "./TransitionProgressIndicator.types";

import styles from "./TransitionProgressIndicator.module.css";

const getTextColor = (status: StepStatus) => {
  switch (status) {
    case "completed":
      return "textSuccess" as const;

    case "in-progress":
      return "textInProgress" as const;

    case "not-started":
      return "textSubtle" as const;

    default:
      return "textSubtle" as const;
  }
};

const TransitionProgressStep: React.FC<{
  step: ProgressStep;
}> = ({ step }) => {
  const status = getStepStatus(step);
  const textColor = getTextColor(status);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={toClassName(styles.progressStep, {
        [styles.completed]: status === "completed",
        [styles.inProgress]: status === "in-progress",
        [styles.notStarted]: status === "not-started",
      })}
      sx={{ width: "100%" }}
    >
      <Text variant="heading1" color={textColor}>
        {step.label}
      </Text>

      <Stack direction="row" alignItems="center" gap={0.5}>
        <Text variant="bodySmall" color={textColor}>
          {step.completed} of {step.total}
        </Text>
        {status === "completed" && (
          <CheckIcon className={styles.completedIcon} />
        )}
        {status === "in-progress" && (
          <HalfFilledCircleIcon className={styles.inProgressIcon} />
        )}
      </Stack>
    </Stack>
  );
};

const TransitionProgressIndicator: React.FC<
  TransitionProgressIndicatorProps
> = ({ steps, className, progressInfo, companyInfo }) => {
  return (
    <Stack direction="column" spacing={2} className={className}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack direction="column" spacing={0}>
          <Text variant="body" color="textSubtle">
            Transition Progress
          </Text>
          {progressInfo}
        </Stack>
        {companyInfo && (
          <Stack direction="column" spacing={0} alignItems="flex-end">
            {companyInfo.teamName && (
              <Text variant="body" color="textSubtle">
                {companyInfo.teamName}
              </Text>
            )}
            <Text variant="body" color="text">
              {companyInfo.advisorName}
            </Text>
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={0.25}
        className={styles.progressBar}
        sx={{
          width: "100%",
        }}
      >
        {steps.length > 0 ? (
          steps.map((step) => (
            <TransitionProgressStep key={step.label} step={step} />
          ))
        ) : (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%", py: 2 }}
          >
            <Text variant="body" color="textSubtle">
              No progress data available
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default TransitionProgressIndicator;
